/**
 * useOrderTracking — live progress of one online order for the customer.
 *
 * Polls GET /api/square-order?orderId=… (Square fulfillment + KDS progress)
 * while the page is visible and the order can still change. Polling is the
 * whole cost of this feature — one Square call and, until the order is ready,
 * one Firestore read each — so it stops the moment the order is picked up or
 * canceled, pauses in a background tab, and slows down for long waits.
 *
 * Returns { status, error }; `status` is null until the first answer.
 */
import { useEffect, useRef, useState } from 'react';
import { getOrderStatus } from './orderApi';
import { isFinalStage } from './orderFlow';

const FAST_MS = 20 * 1000;
const SLOW_MS = 60 * 1000;
const SLOW_AFTER_MS = 30 * 60 * 1000;
const GIVE_UP_MS = 3 * 60 * 60 * 1000;

export default function useOrderTracking(orderId, { enabled = true, onChange } = {}) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    setStatus(null); setError('');
    if (!orderId || !enabled) return undefined;

    let alive = true;
    let timer = null;
    let final = false;
    let lastStage = null;
    const startedAt = Date.now();

    const schedule = () => {
      clearTimeout(timer);
      const age = Date.now() - startedAt;
      if (final || age > GIVE_UP_MS) return;
      timer = setTimeout(tick, age > SLOW_AFTER_MS ? SLOW_MS : FAST_MS);
    };

    async function tick() {
      if (document.visibilityState === 'hidden') return; // resumes on visibilitychange
      try {
        const next = await getOrderStatus(orderId);
        if (!alive) return;
        final = isFinalStage(next.stage);
        setError('');
        setStatus(next);
        if (next.stage !== lastStage) { lastStage = next.stage; onChangeRef.current?.(next); }
      } catch (e) {
        if (alive) setError(e.message || 'Could not refresh the order status');
      }
      if (alive) schedule();
    }

    const onVisible = () => { if (document.visibilityState === 'visible' && !final) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    tick();

    return () => {
      alive = false;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [orderId, enabled]);

  return { status, error };
}
